import React from 'react';

interface MobileFilterModalProps {
  show: boolean;
  filters: any;
  onFilterChange: (filters: any) => void;
  onClose: () => void;
}

const MobileFilterModal: React.FC<MobileFilterModalProps> = ({ 
  show, 
  filters, 
  onFilterChange, 
  onClose 
}) => {
  if (!show) return null;

  const handleFilterChange = (key: string, value: any) => {
    onFilterChange({ [key]: value });
  };

  const handleArrayFilterChange = (key: string, value: any, checked: boolean) => {
    const currentArray = filters[key] || [];
    let newArray;
    
    if (checked) {
      newArray = [...currentArray, value];
    } else {
      newArray = currentArray.filter((item: any) => item !== value);
    }
    
    onFilterChange({ [key]: newArray });
  };

  const handleReset = () => {
    onFilterChange({
      keyword: '',
      skill_ids: [],
      occupation_ids: [],
      industry_ids: [],
      characteristic_ids: [],
      workstyle_types: [],
      availability_types: [],
      price_range: '',
      published_within: undefined
    });
  };

  const handleSearch = () => {
    onClose();
  };

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-fullscreen">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">検索条件</h5>
            <button 
              type="button" 
              className="btn-close" 
              onClick={onClose}
            ></button>
          </div>
          
          <div className="modal-body">
            {/* キーワード検索 */}
            <div className="mb-4">
              <label className="form-label fw-bold">キーワード</label>
              <input
                type="text"
                className="form-control"
                placeholder="例) PHP 渋谷"
                value={filters.keyword || ''}
                onChange={(e) => handleFilterChange('keyword', e.target.value)}
              />
            </div>

            {/* 単価 */}
            <div className="mb-4">
              <label className="form-label fw-bold">単価</label>
              <select
                className="form-select"
                value={filters.price_range || ''}
                onChange={(e) => handleFilterChange('price_range', e.target.value)}
              >
                <option value="">単価を選択してください</option>
                <option value="under_300000">30万円未満</option>
                <option value="300000_and_above">30万円以上</option>
                <option value="400000_and_above">40万円以上</option>
                <option value="500000_and_above">50万円以上</option>
                <option value="600000_and_above">60万円以上</option>
                <option value="700000_and_above">70万円以上</option>
                <option value="800000_and_above">80万円以上</option>
              </select>
            </div>

            {/* 詳細条件 */}
            <div className="mb-4">
              <h6 className="fw-bold mb-3">詳細条件</h6>
              
              {/* 募集中 */}
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="recruiting"
                  checked={true} // 常に募集中のみ表示
                  readOnly
                />
                <label className="form-check-label" htmlFor="recruiting">
                  募集中
                </label>
              </div>

              {/* リモート可 */}
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="mobile_remote"
                  checked={filters.workstyle_types?.includes('remote') || false}
                  onChange={(e) => handleArrayFilterChange('workstyle_types', 'remote', e.target.checked)}
                />
                <label className="form-check-label" htmlFor="mobile_remote">
                  リモート可
                </label>
              </div>

              {/* フルリモート */}
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="mobile_full_remote"
                  checked={filters.workstyle_types?.includes('full_remote') || false}
                  onChange={(e) => handleArrayFilterChange('workstyle_types', 'full_remote', e.target.checked)}
                />
                <label className="form-check-label" htmlFor="mobile_full_remote">
                  フルリモート
                </label>
              </div>

              {/* 勤務可能日数 */}
              <div className="mb-3">
                <label className="form-label fw-bold">勤務可能日数</label>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="mobile_one_day"
                    checked={filters.availability_types?.includes('one_day_in_a_week') || false}
                    onChange={(e) => handleArrayFilterChange('availability_types', 'one_day_in_a_week', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="mobile_one_day">
                    週1日
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="mobile_two_days"
                    checked={filters.availability_types?.includes('two_days_in_a_week') || false}
                    onChange={(e) => handleArrayFilterChange('availability_types', 'two_days_in_a_week', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="mobile_two_days">
                    週2日
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="mobile_three_days"
                    checked={filters.availability_types?.includes('three_days_in_a_week') || false}
                    onChange={(e) => handleArrayFilterChange('availability_types', 'three_days_in_a_week', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="mobile_three_days">
                    週3日
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="mobile_four_days"
                    checked={filters.availability_types?.includes('four_days_in_a_week') || false}
                    onChange={(e) => handleArrayFilterChange('availability_types', 'four_days_in_a_week', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="mobile_four_days">
                    週4日
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="mobile_five_days"
                    checked={filters.availability_types?.includes('five_days_in_a_week') || false}
                    onChange={(e) => handleArrayFilterChange('availability_types', 'five_days_in_a_week', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="mobile_five_days">
                    週5日
                  </label>
                </div>
              </div>

              {/* 新着案件 */}
              <div className="mb-3">
                <label className="form-label fw-bold">新着案件</label>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="mobile_published_within_7"
                    checked={filters.published_within === 7}
                    onChange={(e) => handleFilterChange('published_within', e.target.checked ? 7 : undefined)}
                  />
                  <label className="form-check-label" htmlFor="mobile_published_within_7">
                    直近7日以内
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="mobile_published_within_30"
                    checked={filters.published_within === 30}
                    onChange={(e) => handleFilterChange('published_within', e.target.checked ? 30 : undefined)}
                  />
                  <label className="form-check-label" htmlFor="mobile_published_within_30">
                    直近30日以内
                  </label>
                </div>
              </div>
            </div>
          </div>
          
          <div className="modal-footer">
            <button 
              type="button" 
              className="btn btn-outline-secondary"
              onClick={handleReset}
            >
              リセット
            </button>
            <button 
              type="button" 
              className="btn btn-primary"
              onClick={handleSearch}
            >
              この条件で検索する
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileFilterModal;
